import React from "react";

import { FieldType } from "components/FieldType";
import { CodeExample } from "components/CodeExample";
import { Breadcrumbs } from "components/Breadcrumbs";
import { generateArgumentDescription } from "utils/argumentDescriptions";

// Constants for pagination and field generation
const PAGINATION_ARGS = ['first', 'last', 'after', 'before'];
const EXCLUDED_PAGINATION_ARGS = ['last', 'before', 'after'];

const generateExampleQuery = (fieldName, field, isQuery) => {
  const operation = isQuery ? "query" : "mutation";

  // Generate variable declarations for operation signature
  const getTypeName = (type) => {
    if (type.kind === "NON_NULL") {
      return getTypeName(type.ofType) + "!";
    }
    if (type.kind === "LIST") {
      return "[" + getTypeName(type.ofType) + "]";
    }
    return type.name;
  };

  // Get the root return type name
  const getReturnTypeName = (type) => {
    if (!type) return null;
    if (type.kind === "NON_NULL" || type.kind === "LIST") {
      return getReturnTypeName(type.ofType);
    }
    return type.name;
  };

  const returnTypeName = getReturnTypeName(field.type);
  const isConnectionType = returnTypeName && returnTypeName.endsWith("Connection");

  // Filter out conflicting pagination args - keep only 'first' for simplicity
  const hasPaginationArgs = field.args && field.args.some(a =>
    PAGINATION_ARGS.includes(a.name)
  );

  const filteredArgs = field.args && hasPaginationArgs
    ? field.args.filter(a => !EXCLUDED_PAGINATION_ARGS.includes(a.name))
    : field.args;

  // Format variables with line breaks for readability
  const variableDeclarations = filteredArgs && filteredArgs.length > 0
    ? filteredArgs.length === 1
      ? `($${filteredArgs[0].name}: ${getTypeName(filteredArgs[0].type)})`
      : `(
  ${filteredArgs.map(a => `$${a.name}: ${getTypeName(a.type)}`).join(",\n  ")}
)`
    : "";

  // Format field arguments with line breaks for readability
  const fieldArgs = filteredArgs && filteredArgs.length > 0
    ? filteredArgs.length === 1
      ? `(${filteredArgs[0].name}: $${filteredArgs[0].name})`
      : `(
    ${filteredArgs.map(a => `${a.name}: $${a.name}`).join(",\n    ")}
  )`
    : "";

  // Generate appropriate fields based on return type
  let exampleFields;
  if (isConnectionType) {
    exampleFields = `edges {
      node {
        id
        # Add other fields you need
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }`;
  } else {
    exampleFields = `id
    # Add other fields you need`;
  }

  return `${operation}${variableDeclarations} {
  ${fieldName}${fieldArgs} {
    ${exampleFields}
  }
}`;
};

const generateExampleVariables = (field) => {
  if (!field.args || field.args.length === 0) return null;

  // Filter out conflicting pagination args - keep only 'first' for simplicity
  const hasPaginationArgs = field.args.some(a =>
    PAGINATION_ARGS.includes(a.name)
  );

  const filteredArgs = hasPaginationArgs
    ? field.args.filter(a => !EXCLUDED_PAGINATION_ARGS.includes(a.name))
    : field.args;

  const variables = {};
  filteredArgs.forEach(arg => {
    const typeName = arg.type.name || (arg.type.ofType && arg.type.ofType.name);
    if (typeName === "ID") variables[arg.name] = "1";
    else if (typeName === "String") variables[arg.name] = "example";
    else if (typeName === "Int") variables[arg.name] = 1;
    else if (typeName === "Boolean") variables[arg.name] = true;
    else variables[arg.name] = {};
  });

  return JSON.stringify(variables, null, 2);
};

const Type = ({ pathContext: { type, name, kind, description, fields } }) => {
  const isQuery = type === "QUERY";
  const contextName = isQuery ? name.slice(0, -7) : name.slice(0, -9);

  const breadcrumbItems = [
    { label: isQuery ? "Queries" : "Mutations", path: null },
    { label: contextName, path: null }
  ];

  return (
    <div className="page">
      <section>
        <div className="section-text">
          <Breadcrumbs items={breadcrumbItems} />
          {isQuery ? (
            <h1>{contextName} Queries</h1>
          ) : (
            <h1>{contextName} Mutations</h1>
          )}
          {description && <p>{description}</p>}
        </div>
        <div className="section-example on">
          <div className="section-example-info">
            These {isQuery ? "queries" : "mutations"} allow you to interact with {contextName.toLowerCase()} data.
          </div>
        </div>
      </section>
      {fields &&
        fields.map(f => {
          const exampleQuery = generateExampleQuery(f.name, f, isQuery);
          const exampleVariables = generateExampleVariables(f);

          return (
            <section key={f.name} id={f.name}>
              <div className="section-text">
                <div className="operation-header">
                  <h3 className="operation-name">{f.name}</h3>
                  <div className="operation-return-type">
                    Returns: <FieldType field={f} />
                  </div>
                </div>
                {f.description && <p className="operation-description">{f.description}</p>}

                {f.args && f.args.length > 0 && (
                  <div className="operation-arguments">
                    <h4>Arguments</h4>
                    <table>
                      <thead>
                        <tr>
                          <th>Argument</th>
                          <th>Type</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {f.args.map(a => (
                          <tr key={a.name}>
                            <td>
                              <code className="arg-name">{a.name}</code>
                              {a.type.kind === "NON_NULL" && (
                                <span className="required-badge">required</span>
                              )}
                            </td>
                            <td>
                              <FieldType field={a} />
                            </td>
                            <td>
                              {a.isDeprecated && (
                                <span className="deprecated-warning">
                                  <strong>DEPRECATED:</strong> {a.deprecationReason}&nbsp;
                                </span>
                              )}
                              {a.description || generateArgumentDescription(a.name, a.type)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="section-example on">
                <div className="section-example-area">
                  <CodeExample
                    title="Example Query"
                    code={exampleQuery}
                    language="graphql"
                  />
                </div>
                {exampleVariables && (
                  <div className="section-example-area">
                    <CodeExample
                      title="Variables"
                      code={exampleVariables}
                      language="json"
                    />
                  </div>
                )}
              </div>
            </section>
          );
        })}
    </div>
  );
};

export default Type;



// WEBPACK FOOTER //
// ./src/templates/queries.js